/**
 * ---------------------------------------------------------------------------
 * FullePaymentsMethodsEndorderComponent
 * ---------------------------------------------------------------------------
 *
 * RÔLE MÉTIER
 * --------------
 * Ce composant gère l’affichage et la sélection des moyens de paiement
 * lors de la finalisation d’une commande (endorder).
 *
 * Il est utilisé :
 *  - sur la page de fin de commande
 *  - lors d’une recharge
 *  - dans certains encaissements de crédits
 *
 * RESPONSABILITÉS
 * ------------------
 * - Afficher dynamiquement les moyens de paiement disponibles
 * - Gérer la sélection du mode de paiement
 * - Afficher les informations de carte enregistrée
 * - Gérer les cas particuliers (secure_counter, cagnotte, crédit, etc.)
 * - Interagir avec les composants enfants (ex: app-cards-endorder)
 *
 * ⚠ IMPORTANT
 * -----------
 * Ce composant ne décide PAS quels moyens sont disponibles.
 * Il se base sur une liste `paymentsMethods` déjà préparée en amont.
 *
 * Toute logique métier lourde doit rester :
 *  - dans le parent
 *  - ou dans un service
 *
 * Ce composant est essentiellement un composant d’orchestration UI.
 *
 * FLUX SIMPLIFIÉ
 * -----------------
 * 1. Le parent fournit :
 *      - currPOS
 *      - currSM
 *      - clientCards
 *      - paymentsMethods
 *
 * 2. Le composant affiche les moyens visibles
 *
 * 3. L’utilisateur sélectionne un mode
 *
 * 4. Le composant émet l’information au parent
 *
 * ZONES SENSIBLES
 * -------------------
 * - secure_counter (nécessite une carte)
 * - creditMode
 * - clientCumul
 * - dépendances implicites sur currSM.enable_delivery
 *
 * Toute modification ici peut impacter :
 * - Stripe
 * - Yavin
 * - MyPOS
 * - Nepting
 * - Paiement comptoir
 *
 * Toujours tester tous les parcours avant merge.
 *
 * ---------------------------------------------------------------------------
 */

import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { StripeCardScaComponent } from 'src/app/components/dialogs/stripe-card-sca/stripe-card-sca.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToolsService } from 'src/app/services/tools/tools.service';
import { CssService } from "../../../../../services/css-service";
import { Subscription } from 'rxjs';
import { PointOfSaleDetails } from 'src/app/services/api/fulle/fulle-pointOfSale/fulle-pointOfSale.service.interface';

type PaymentType =
  | 'online'
  | 'card'
  | 'secure_counter'
  | 'stripe'
  | 'online_paypal'
  | 'payzen'
  | 'counter'
  | 'touchTerminal'
  | 'myPos'
  | 'yavin'
  | 'nepting';

interface PaymentMethodUi {
  id: number;
  name: string;
  paiment_type: PaymentType;
  visible: boolean;
}

type CardBrand = 'visa' | 'mastercard' | string;

interface StripeCardDetails {
  brand: CardBrand;
  last4: string;
  logo_src?: string; // parfois absent selon la marque
}

/** Carte “minimale” (on ne sait pas tout, mais on sait ce qu’on utilise). */
interface ClientCard {
  type?: 'card' | string;      // tu testes currCard$.type
  card: StripeCardDetails;      // tu utilises currCard$.card.xxx dans le HTML
  [k: string]: unknown;
}

/** User “minimal” (car tu ne l’utilises que comme truthy). */
type AuthUser = Record<string, unknown>;

/** Sale method “minimal” selon ce que tu lis dans le composant. */
interface SaleMethodLike {
  disable_authentification?: boolean;
  enable_counter_payment: number;
  enable_secure_counter_payment: number;
  enable_online_payment: number;

  enable_delivery?: number;
  payment_type?: PaymentType | null;
  enable_device_check?: number;
}

/** Mode crédit : tu passes parfois un objet { point_of_sale: ... } */
interface CreditModePosWrapper {
  point_of_sale?: PointOfSaleDetails;
  [k: string]: unknown;
}

/** Résultat de la dialog StripeCardScaComponent (d’après ton code) */
type StripeCardDialogResult =
  | { from: 'saved'; cards: ClientCard[] }
  | { from: 'momentary'; card: ClientCard }
  | null
  | undefined;

@Component({
  selector: 'app-fulle-payments-methods-endorder',
  templateUrl: './fulle-payments-methods-endorder.component.html',
  styleUrls: ['./fulle-payments-methods-endorder.component.css']
})
/**
 * Composant d'affichage des moyens de paiement sur la page de fin de commande, recharge, et encaissement crédits
 */
export class FullePaymentsMethodsEndorderComponent implements OnInit, OnChanges {
  @Input() currPOS: PointOfSaleDetails | null = null;
  @Input() currSM: SaleMethodLike | null = null;

  @Input() clientCards: ClientCard[] = [];
  @Input() clientCardLoader = false;

  // ton code l’appelle currCard$ mais c’est un objet, pas un Observable => on garde le nom mais on type correctement
  @Input() currCard$: ClientCard | null = null;

  @Input() currPaymentChoice: PaymentType | null = null;

  // recharge / credit
  @Input() currMerchant: PointOfSaleDetails | null = null;
  @Input() deviceChange = false;

  @Input() rechargeMode = false;
  @Input() creditMode = false;

  @Input() clientCumul = -1;
  @Input() payment_type: PaymentType | null = null;

  @Output() setPaymentChoiceEvent = new EventEmitter<PaymentType | null>();
  @Output() openCardMethodsDialEvent = new EventEmitter<'onlycard'>();
  @Output() setClientCardsDialEvent = new EventEmitter<ClientCard[]>();
  @Output() payMethodEvent = new EventEmitter<PaymentMethodUi[]>();

  currPaymentMethod: PaymentType | '' = '';
  loader = false;

  user$: AuthUser | null = null;
  private userSub?: Subscription;

  paymentsMethods: PaymentMethodUi[] = [
    { id: 0, name: 'card', paiment_type: 'online', visible: false },
    { id: 1, name: 'secure_counter', paiment_type: 'secure_counter', visible: false },
    { id: 2, name: 'stripe', paiment_type: 'stripe', visible: false },
    { id: 3, name: 'online_paypal', paiment_type: 'online_paypal', visible: false },
    { id: 4, name: 'payzen', paiment_type: 'payzen', visible: false },
    { id: 5, name: 'counter', paiment_type: 'counter', visible: false },
    { id: 6, name: 'touchTerminal', paiment_type: 'touchTerminal', visible: false },
    { id: 7, name: 'myPos', paiment_type: 'myPos', visible: false },
    { id: 8, name: 'yavin', paiment_type: 'yavin', visible: false },
    { id: 9, name: 'nepting', paiment_type: 'nepting', visible: false },
  ];

  myDial?: MatDialogRef<StripeCardScaComponent, StripeCardDialogResult>;
  init = false;

  constructor(
    private tools: ToolsService,
    private dialog: MatDialog,
    private media: MediaObserver,
    public cssService: CssService,
    private auth: AuthService
  ) {
    this.userSub = this.auth.getUser.subscribe((user: AuthUser) => {
      this.user$ = user;
    });
  }

  ngOnInit() {
    // console.log("ngOnInit() is called")
    // console.log("this.currPOS = ", this.currPOS);
    this.init = true;
    if (this.payment_type) {
      this.setPaymentMethod(this.payment_type);
    }
  }

  /**
   * @description  boucle pour Selectionne une methode de paiement a voir
   */
  getVisiblePaymentsMethods(): void {
    if (this.currPOS && this.currSM) {
      this.paymentsMethods.forEach((element) => {
        this.checkvisibleStatePaymentmethod(element);
      });

      this.payMethodEvent.emit(this.paymentsMethods);

      const onePayMethod = this.checkOnePayMethod();
      if (onePayMethod.length <= 1) {
        setTimeout(() => {
          if (onePayMethod.length === 0) {
            this.setPaymentMethod(null);
            return;
          }
          const pm = onePayMethod[0].paiment_type;
          this.setPaymentMethod(pm);
        }, 0);
      }
    }
  }

  checkOnePayMethod(): PaymentMethodUi[] {
    return this.paymentsMethods.filter((m) => m.visible);
  }


  /**
   * @description Selectionne une methode de paiement a voir
   */
  checkvisibleStatePaymentmethod(el: PaymentMethodUi): PaymentMethodUi {
    if (!this.currSM || !this.currPOS) {
      el.visible = false;
      return el;
    }

    if (this.currSM.disable_authentification) {
      this.currPOS.touchTerminal = !!this.tools.getStor('devicePaybridge');
    }

    // console.log("el.paiment_type : ", el.paiment_type)

    switch (el.paiment_type) {
      case 'counter':
        el.visible =
          !this.currMerchant &&
          this.currSM.enable_counter_payment > 0 &&
          this.currSM.enable_secure_counter_payment < 1;
        break;

      case 'secure_counter':
        el.visible =
          !this.currPOS.touchTerminal &&
          !this.currMerchant &&
          !!this.currSM.enable_online_payment &&
          this.currSM.enable_counter_payment > 0 &&
          this.currSM.enable_secure_counter_payment > 0 &&
          !!this.currPOS.stripeAccount;
        break;

      case 'online':
        el.visible =
          !this.currPOS.touchTerminal &&
          !!this.currSM.enable_online_payment &&
          !!this.user$ &&
          !!this.currPOS.stripeAccount &&
          this.clientCards &&
          this.clientCards.length > 0 &&
          !this.clientCardLoader &&
          !!this.currCard$ &&
          this.currCard$.type === 'card';
        break;

      case 'touchTerminal':
        el.visible = !!this.currSM.enable_online_payment && !!this.currPOS.touchTerminal;
        break;

      case 'nepting':
        el.visible =
          !this.currPOS.touchTerminal &&
          !!this.currSM.enable_online_payment &&
          (this.currPOS.payment_methods?.[0]?.type === 1);
        break;

      case 'myPos':
        el.visible =
          !this.currPOS.touchTerminal &&
          !!this.currSM.enable_online_payment &&
          (this.currPOS.payment_methods?.[0]?.type === 2);
        break;

      case 'stripe':
        el.visible =
          !this.currPOS.touchTerminal &&
          !!this.currSM.enable_online_payment &&
          (this.currPOS.payment_methods?.[0]?.type === 3);
        break;

      // 4 est archived

      case 'online_paypal':
        el.visible =
          !this.currPOS.touchTerminal &&
          !!this.currSM.enable_online_payment &&
          (this.currPOS.payment_methods?.[0]?.type === 5);
        break;

      case 'payzen':
        el.visible =
          !this.currPOS.touchTerminal &&
          !this.rechargeMode &&
          !!this.currSM.enable_online_payment &&
          (this.currPOS.payment_methods?.[0]?.type === 6);
        break;

      // 7 est archived

      case 'yavin':
        el.visible =
          !this.currPOS.touchTerminal &&
          !!this.currSM.enable_online_payment &&
          (this.currPOS.payment_methods?.[0]?.type === 8);
        break;

      default:
        el.visible = false;
    }

    return el;
  }

  /**
   * @description Selectionne une methode de paiement
   */

  setPaymentMethod(pm: PaymentType | null): void {
    if (this.creditMode && (this.clientCumul ?? 0) >= 0 && !this.payment_type) {
      return;
    }

    this.payment_type = null;

    let resolved: PaymentType | null = pm;

    if (pm === 'online' && this.currCard$?.type === 'card') {
      resolved = 'card';
    } else if (pm === 'online' && !this.currCard$) {
      resolved = 'stripe';
    }

    setTimeout(() => {
      this.currPaymentMethod = resolved ?? '';

      const paymentMethod = resolved === 'card' ? 'online' : resolved;
      this.setPaymentChoiceEvent.emit(paymentMethod);
    }, 0);

    if (
      resolved === 'secure_counter' &&
      !this.currCard$ &&
      this.clientCards.length === 0
    ) {
      this.openDialogCard();
    }
  }

  /**
   * Des modes de réglement sont visibles ?
   * true ou false
   */
  hasVisiblePaymentMethods(): boolean {
    return this.paymentsMethods.some((m) => m.visible);
  }

  modeRechargeOrCredit(currPos?: PointOfSaleDetails): void {
    if (this.currMerchant || currPos) {
      this.currSM = {
        enable_counter_payment: 0,
        enable_secure_counter_payment: 0,
        enable_online_payment: 1,
      };

      this.currPOS = this.creditMode && currPos ? currPos : this.currMerchant;
      this.currPaymentChoice = this.currSM.payment_type ?? null;
    }
  }

  /**
   * @description lors de changement
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceChange']?.currentValue) {
      this.getVisiblePaymentsMethods();
    }

    if (changes['creditMode']?.currentValue !== undefined) {
      this.creditMode = !!changes['creditMode'].currentValue;
    }

    if (changes['payment_type']?.currentValue !== undefined) {
      this.payment_type = changes['payment_type'].currentValue as PaymentType;
    }

    if (changes['clientCumul']?.currentValue !== undefined) {
      this.clientCumul = Number(changes['clientCumul'].currentValue);
      this.modeRechargeOrCredit();
    }

    if (changes['currMerchant']?.currentValue && !this.creditMode) {
      this.currMerchant = changes['currMerchant'].currentValue as PointOfSaleDetails;
      this.modeRechargeOrCredit();
    }

    if (changes['currSM']?.currentValue) {
      this.currSM = changes['currSM'].currentValue as SaleMethodLike;
    }

    if (changes['currPOS']?.currentValue) {
      const currPOS = changes['currPOS'].currentValue as PointOfSaleDetails | CreditModePosWrapper;
      if (this.creditMode && (currPOS as CreditModePosWrapper).point_of_sale) {
        this.modeRechargeOrCredit((currPOS as CreditModePosWrapper).point_of_sale);
      } else {
        this.currPOS = currPOS as PointOfSaleDetails;
      }
    }

    if (changes['clientCardLoader']?.currentValue !== undefined) {
      this.clientCardLoader = !!changes['clientCardLoader'].currentValue;
    }

    if (changes['clientCards']?.currentValue) {
      this.clientCards = changes['clientCards'].currentValue as ClientCard[];

      if (
        this.init &&
        this.currPaymentChoice === 'secure_counter' &&
        this.clientCards.length === 0
      ) {
        this.init = false;
        this.openDialogCard();
      }
    }

    if (changes['currCard$']?.currentValue !== undefined) {
      this.currCard$ = changes['currCard$'].currentValue as ClientCard | null;
      if (this.currPaymentChoice) {
        this.setPaymentMethod(this.currPaymentChoice);
      }
    }

    if (changes['currPaymentChoice']?.currentValue) {
      this.currPaymentChoice = changes['currPaymentChoice'].currentValue as PaymentType;
      this.setPaymentMethod(this.currPaymentChoice);
    }

    this.getVisiblePaymentsMethods();
  }

  /**
   * @description  set ClientCards
   */
  setClientCards(cards: ClientCard[]): void {
    this.clientCards = cards;
  }

  /**
   * @description  choix de diffrentes dialog creation card ou modification (delete)
   */
  openDialogCard(): void {
    if (this.clientCards.length === 0) {
      const secureCounter = this.paymentsMethods.find((m) => m.paiment_type === 'secure_counter');
      if (secureCounter?.visible) {
        this.openDialogCreateCard();
      }
    } else {
      this.openCardMethodsDialEvent.emit('onlycard');
    }
  }


  /**
   * @description  ouvre la dialogue de creation
   */
  openDialogCreateCard(): void {
    if (this.myDial && this.myDial.getState() === MatDialogState.OPEN) {
      return;
    }

    const myConf = new MatDialogConfig();
    if (this.media.isActive('xs')) {
      myConf.width = '100%';
      myConf.height = '100%';
    } else {
      myConf.width = '550px';
      myConf.height = '80%';
    }

    this.myDial = this.dialog.open<StripeCardScaComponent, unknown, StripeCardDialogResult>(
      StripeCardScaComponent,
      myConf
    );

    this.myDial.afterClosed().subscribe((result) => {
      if (!result) return;

      if (result.from === 'saved') {
        this.setClientCards(result.cards);
      } else if (result.from === 'momentary') {
        this.clientCards = [...this.clientCards, result.card];
      }

      this.setClientCardsDialEvent.emit(this.clientCards);
    });
  }


}

